import { useCallback, useMemo, useEffect } from 'react'
import {
  SEARCH_FILTER_TYPE,
  type SEARCH_FILTER_TYPE_FACET,
} from '@/features/shared/constants/global'
import { type Facet } from '@/features/shop/services/Search/types'
import {
  findActiveStoreFilters,
  isCurrentCategoryActive,
  findCurrentCategoryByParams,
} from '@/features/shop/services/Search/utils'
import { useDeliveryStores } from '@/features/shop/services/ShoppingStore/hooks'
import { useValidSearchParams } from '@/features/shared/hooks/search'
import { useUserId } from '@/features/account/services/User/hooks'
import { medallia } from '@/lib/medallia'
import { useDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'

const { SORT_BY, ACTIVE_DEALS, CATEGORY, BRAND, STORE, ON_SALE, FLAVOR } = SEARCH_FILTER_TYPE

export const useActiveFiltersLengths = (facets: Facet[]): { [k in SEARCH_FILTER_TYPE]: number } => {
  const storesInZipStores = useDeliveryStores()
  const searchParams = useValidSearchParams()
  const findActiveFiltersByFacetName = useFindActiveArrayFiltersForFacetName()

  return useMemo(() => {
    const currentCategory = findCurrentCategoryByParams(facets, searchParams)
    const activeStoreFilters = findActiveStoreFilters(storesInZipStores, searchParams)
    let activeStoreCount = 0
    if (activeStoreFilters.length <= storesInZipStores.length) {
      activeStoreCount = activeStoreFilters.length
    }
    const { active_deals, sort = '' } = searchParams
    return {
      [SORT_BY]: sort ? 1 : 0,
      [ACTIVE_DEALS]: active_deals ? 1 : 0,
      [CATEGORY]: isCurrentCategoryActive(searchParams, currentCategory) ? 1 : 0,
      [BRAND]: findActiveFiltersByFacetName(facets, BRAND).length,
      [STORE]: activeStoreCount,
      [ON_SALE]: 0,
      [FLAVOR]: findActiveFiltersByFacetName(facets, FLAVOR).length,
    }
  }, [facets, storesInZipStores, searchParams, findActiveFiltersByFacetName])
}

export const useTotalActiveFiltersLength = (facets: Facet[]): number => {
  const activeFiltersLengths = useActiveFiltersLengths(facets)
  return Object.values(activeFiltersLengths).reduce((acc, value) => acc + value, 0)
}

export const useIsBrowse = () => {
  const { query, category_id, promotion_category_id, category_ids, new_deals } =
    useValidSearchParams()
  return Boolean(
    !query && (category_id || promotion_category_id || new_deals) && !category_ids?.length,
  )
}

export const useFindActiveArrayFiltersForFacetName = () => {
  const searchParams = useValidSearchParams()

  return useCallback(
    (facets: Facet[], facetName: SEARCH_FILTER_TYPE_FACET) => {
      const { [facetName]: filtersFromParams } = searchParams
      if (!filtersFromParams) return []

      const facet = facets.find((f) => f.facet === facetName)
      const itemNames = facet?.items.map((i) => i.name) ?? []
      return itemNames.filter((item) => filtersFromParams.includes(item))
    },
    [searchParams],
  )
}

export const useMedalliaSearchParams = ({
  store_id,
  metro_id,
  store_location_id,
  query,
  product_category,
  searchType,
  retailer,
}: {
  store_id?: number | null
  metro_id?: number | null
  store_location_id?: number | null
  query?: string
  product_category?: string
  searchType?: string
  retailer?: string
}) => {
  const userId = useUserId()
  const { state, zip_code } = useDefaultShoppingAddress()

  useEffect(() => {
    if (userId) {
      medallia.updatePageView({
        store_id,
        metro_id,
        store_location_id,
        query,
        product_category,
        searchType,
        state,
        zip_code,
        customerID: userId,
        retailer,
      })
      return () => {
        medallia.updatePageView({
          query: '',
          product_category: '',
        })
      }
    }
  }, [
    userId,
    store_id,
    metro_id,
    store_location_id,
    query,
    product_category,
    searchType,
    state,
    zip_code,
    retailer,
  ])
}
